import { Draggable, DraggableProvided } from '@hello-pangea/dnd';
import cx from 'classnames';
import Tooltip from 'js/components/elements/mui/Tooltip';
import LoadableTransformDSLEditor from 'js/components/recipeBuilder/LoadableTransformDSLEditor';
import filterFunctions from 'js/constants/filterFunctions';
import { registerTransformDSLCompletion } from 'js/constants/monaco-transform-dsl';
import { ReactComponent as Burger } from 'new/components/icons/burger.svg';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { RemoveIconButton } from 'new/elements/Button/RemoveIconButton';
import { reorder } from 'new/utils';
import React, { useEffect, useState } from 'react';
import { DraggableVerticalList } from '../DraggableVerticalList';
import classes from './DataSetSplitDialog.module.scss';

export interface ExpressionItem {
  __id: string;
  code: string;
  name: string;
  discard?: boolean;
  dataSetId?: string;
}

interface Props {
  isReadOnly?: boolean;
  highlightedId?: string | null;
  expressions: ExpressionItem[];
  setExpressions: (expressions: ExpressionItem[]) => void;
  parentDataSetSchema: any[];
  parsingErrors?: Record<string, string>;
}

const MAX_EXPRESSIONS = 5;

export default function ExpressionsEditor({
  isReadOnly = false,
  highlightedId,
  expressions,
  setExpressions,
  parentDataSetSchema,
  parsingErrors,
}: Props) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const expressionLimitReached = expressions.length >= MAX_EXPRESSIONS;

  useEffect(() => {
    if (!isReadOnly) {
      registerTransformDSLCompletion(filterFunctions, parentDataSetSchema);
    }
  }, [parentDataSetSchema]);

  const onAddExpression = () => {
    const newExpressions = [
      ...expressions,
      {
        __id: crypto.randomUUID(),
        code: '',
        name: 'Output ' + (expressions.length + 1),
      },
    ];
    setExpressions(newExpressions);
    setFocusedIndex(newExpressions.length - 1);
  };

  const onRemoveExpression = (event) => {
    const { index } = event.currentTarget.dataset;
    const newExpressions = [...expressions];
    newExpressions.splice(index, 1);
    setExpressions(newExpressions);

    if (focusedIndex >= newExpressions.length) {
      setFocusedIndex(newExpressions.length - 1);
    }
  };

  const onChangeExpression = (value: string, index: number) => {
    const newExpressions = [...expressions];
    newExpressions[index] = {
      ...newExpressions[index],
      code: value,
    };
    setExpressions(newExpressions);
  };

  const onFocusExpression = (index: number) => {
    setFocusedIndex(index);
  };

  const onBlurOutside = () => {
    setFocusedIndex(-1);
  };

  const onMoveFocus = (offset: number) => {
    const newFocusedIndex = focusedIndex + offset;

    if (newFocusedIndex >= 0 && newFocusedIndex < expressions.length) {
      setFocusedIndex(newFocusedIndex);
    }
  };

  const onDragEnd = ({ source, destination }) => {
    if (typeof destination?.index !== 'undefined') {
      setExpressions(reorder(expressions, source?.index, destination?.index));
    }
  };

  const renderDraggableList = (list: ExpressionItem[]) => {
    return list.map((item, index) =>
      isReadOnly ? (
        renderListItem(list, item, index)
      ) : (
        <Draggable index={index} key={item.__id} draggableId={item.__id}>
          {(provided) => renderListItem(list, item, index, provided)}
        </Draggable>
      ),
    );
  };

  const renderListItem = (
    list: ExpressionItem[],
    { __id, code, dataSetId }: ExpressionItem,
    index: number,
    provided: DraggableProvided = null,
  ) => {
    return (
      <li
        key={__id}
        id={__id}
        ref={provided?.innerRef || null}
        {...(provided?.draggableProps || {})}
      >
        <div
          className={cx(classes.listItem, {
            [classes.hasError]: !!parsingErrors?.[code],
            [classes.highlighted]: highlightedId === dataSetId,
          })}
        >
          {/* @ts-ignore */}
          <Tooltip tooltipTheme="fieldInfo" title={parsingErrors?.[code] || undefined}>
            <div className={classes.listItemTopRow}>
              {!isReadOnly && (
                <div className={classes.dragHandle} {...(provided?.dragHandleProps || {})}>
                  <Burger />
                </div>
              )}
              <LoadableTransformDSLEditor
                isReadOnly={isReadOnly}
                monacoOptions={{
                  placeholder: 'Type a condition, e.g. IsGreaterThan ( $(quantity), 100 )',
                }}
                value={code}
                index={index}
                expandedRecipeIndex={focusedIndex}
                onNewRule={onAddExpression}
                onChange={(newValue) => onChangeExpression(newValue, index)}
                onFocusRule={onFocusExpression}
                onBlurOutside={onBlurOutside}
                onNavigateRules={onMoveFocus}
              />
              <div className={classes.targetNexsetInline}>
                <span className={classes.targetLabel}>{dataSetId ? `to` : ''}</span>
                {dataSetId ? (
                  <div className={classes.nexsetIdChip}>
                    <NexsetIcon fill="black" />
                    <span className={classes.nexsetId}>ID {dataSetId}</span>
                  </div>
                ) : (
                  <div className={classes.nexsetNumberChip}>
                    <span className={classes.nexsetNumber}>#{index + 1}</span>
                  </div>
                )}
              </div>
              {!isReadOnly && (
                <RemoveIconButton
                  tooltipText={
                    list.length === 1 ? 'Cannot remove the last condition' : 'Remove Condition'
                  }
                  disabled={list.length === 1}
                  additionalProps={{ 'data-index': index }}
                  onClickRemove={onRemoveExpression}
                />
              )}
            </div>
          </Tooltip>
        </div>
      </li>
    );
  };

  return (
    <div>
      <DraggableVerticalList
        items={expressions}
        listClassname="filters-list"
        onDragEnd={onDragEnd}
        renderDraggableList={renderDraggableList}
      />
      {/* @ts-ignore */}
      {!isReadOnly && (
        <Tooltip
          title={expressionLimitReached ? 'Maximum number of conditions reached' : undefined}
        >
          <span>
            <button
              className={classes.addConditionButton}
              disabled={expressionLimitReached}
              onClick={onAddExpression}
            >
              + Add condition
            </button>
          </span>
        </Tooltip>
      )}
    </div>
  );
}
